// Drop-in replacement for `localStorage` that survives the contexts
// where the native object throws synchronously on access.
//
// WHY THIS EXISTS
// ---------------
// Mobile Safari (iOS 18.7+) raises `SecurityError` on `localStorage`
// access in several scenarios: private browsing, partitioned storage,
// in-app browsers (Instagram, Facebook, iMessage preview), some
// Cross-origin embed contexts. Until 2026-05-19 the app's HTML was
// browser-cached so module-load only happened once per "first visit
// in a session"; commit 135e744 (Cache-Control no-store on the HTML
// shell) made every navigation a fresh module load — turning a rare
// trigger into a daily Sentry top-issue.
//
// Supabase's auth-js already has a `supportsLocalStorage()` probe
// (auth-js/dist/main/lib/helpers.js) that falls back to in-memory
// when localStorage isn't writable — but it's only consulted when
// callers omit the `storage` option. The repo's
// `integrations/supabase/client.ts` passed `localStorage` directly,
// which bypassed that probe and crashed at module load. This
// utility solves that and the parallel problem in app code that
// uses localStorage for cached sessions / dismissed banners /
// offline data without a defensive wrapper.
//
// DESIGN
// ------
// - Implements the full `Storage` Web API shape (Liskov) so it can
//   be handed to anything that expects a `Storage` object — most
//   importantly Supabase's auth.storage.
// - Wraps every method in try/catch with a sane no-op / null
//   fallback. Read failures return null; write failures silently
//   drop. Callers don't need to know the underlying store is
//   degraded.
// - On construction, probes the backing store with a write-then-
//   delete round-trip. If the probe throws, the wrapper transparently
//   substitutes an in-memory Map — Liskov-compatible, lasts until
//   tab close, no data persists across reloads (acceptable: the
//   data was never going to persist on this browser anyway).
// - The `safeLocalStorage` default export resolves the global
//   `localStorage` once, defensively (the access itself can throw).
//   Tests can pass an explicit backing via `createSafeStorage`.

// Subset of the DOM `Storage` interface this module implements and
// consumes. Declared explicitly so the file has no DOM-lib dependency
// and stays trivially mockable in tests.
export interface StorageLike {
  readonly length: number;
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  clear(): void;
  key(index: number): string | null;
}

const PROBE_KEY = "__safe_storage_probe__";

// In-memory implementation of `StorageLike`. Used as the fallback
// when the real backing is unavailable or throws. Spec-compliant
// enough for the call-sites in this repo: full read/write/delete +
// length + key(index).
const createMemoryStorage = (): StorageLike => {
  const store = new Map<string, string>();
  return {
    get length() {
      return store.size;
    },
    getItem(key) {
      return store.has(key) ? (store.get(key) as string) : null;
    },
    setItem(key, value) {
      store.set(key, String(value));
    },
    removeItem(key) {
      store.delete(key);
    },
    clear() {
      store.clear();
    },
    key(index) {
      if (!Number.isInteger(index) || index < 0) return null;
      let i = 0;
      for (const k of store.keys()) {
        if (i === index) return k;
        i++;
      }
      return null;
    },
  };
};

// Returns true iff `backing` exists, is callable, AND a write probe
// round-trips without throwing. A `null`/`undefined` backing or any
// thrown exception (SecurityError, QuotaExceededError, anything else)
// flips this to false and the caller substitutes the in-memory store.
const isUsable = (backing: StorageLike | null | undefined): backing is StorageLike => {
  if (!backing) return false;
  try {
    backing.setItem(PROBE_KEY, PROBE_KEY);
    backing.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
};

// Look up the global `localStorage` defensively. Some Safari contexts
// throw `SecurityError` *just from accessing the property* — even
// before any read/write — so the lookup itself needs to live in a
// try/catch.
const resolveDefaultBacking = (): StorageLike | null => {
  try {
    if (typeof globalThis === "undefined") return null;
    const ls = (globalThis as { localStorage?: StorageLike }).localStorage;
    return ls ?? null;
  } catch {
    return null;
  }
};

// Build a `StorageLike` that wraps `backing` with per-method try/catch
// and falls back to in-memory when the backing isn't probe-safe.
//
// Exposed for tests + advanced consumers that want to wrap a
// non-default backing (e.g. `sessionStorage`, a mock, an embedded
// iframe's storage). Most app code should use the `safeLocalStorage`
// singleton below.
export const createSafeStorage = (
  backing: StorageLike | null | undefined = resolveDefaultBacking(),
): StorageLike => {
  const active: StorageLike = isUsable(backing) ? backing : createMemoryStorage();
  return {
    get length() {
      try {
        return active.length;
      } catch {
        return 0;
      }
    },
    getItem(key) {
      try {
        return active.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      try {
        active.setItem(key, value);
      } catch {
        /* swallow — degraded write is preferable to a crash */
      }
    },
    removeItem(key) {
      try {
        active.removeItem(key);
      } catch {
        /* swallow */
      }
    },
    clear() {
      try {
        active.clear();
      } catch {
        /* swallow */
      }
    },
    key(index) {
      try {
        return active.key(index);
      } catch {
        return null;
      }
    },
  };
};

// Single shared instance for normal app usage. Resolved eagerly at
// module load (which is itself defensive — `resolveDefaultBacking`
// catches the throwing-`localStorage` access path that caused the
// original SecurityError reports).
export const safeLocalStorage: StorageLike = createSafeStorage();
